import { DATA_API_SERVICE } from "@/appConfig";
import APIStation from "@/api/auth/station";
import Pricing, { PenaltyUnit, PricingAPIResponse } from "@/classes/pricing";
import { EntityType } from "@/classes/entity";
import StationTransaction from "@/classes/stationTransaction";
import StationSubTransaction, { StationSubTransactionAPIResponse } from "@/classes/stationSubTransaction";
import PricingHistoryItem from "@/classes/pricingHistoryItem";
import sortBy from "lodash/sortBy";
import subDays from "date-fns/subDays";
import endOfDay from "date-fns/endOfDay";
import API from "@/api/api";
import apolloClient from "@/api/charge";
import { gql } from "@apollo/client/core";

function mapPricingDataFromAPI(entityType: EntityType, entityId: string, pricingResponse: PricingAPIResponse) {
  // We either get back an active policy or an inherited from policy
  const policy = pricingResponse.activePolicy ? pricingResponse.activePolicy! : pricingResponse.inheritedFrom!;

  // If we have no policy, simply create a new default pricing policy
  if (!policy) {
    let inheritPricing = true;
    if (entityType === EntityType.ORGANIZATION) {
      inheritPricing = false;
    }
    return new Pricing({
      inheritPricing,
      entity: entityType,
      entityId: entityId
    });
  }

  // Otherwise create a pricing policy using either the inherited from or activePolicy values
  return new Pricing({
    ...policy,
    inheritedFromDetails: pricingResponse.inheritedFrom || undefined,
    inheritPricing: !pricingResponse.activePolicy && !!pricingResponse.inheritedFrom,
    activePolicyId: pricingResponse.activePolicy?.id,
    id: pricingResponse.inheritedFrom ? "" : pricingResponse.activePolicy!.id,
    entity: entityType,
    entityId: entityId,
    createdAt: new Date(policy.createdAt),
    createdBy: policy.createdBy,
    inactivatedAt: policy.inactivatedAt ? new Date(policy.inactivatedAt) : null,
    penaltyUnit: PenaltyUnit.hour
  });
}

export async function getStationPricing(stationId: string) {
  const station = await APIStation.getStationById(stationId, false);
  const stationPricing = await getPricingGeneric(EntityType.STATION, stationId);
  if (stationPricing.inheritPricing) stationPricing.isFree = false;
  else stationPricing.isFree = station.isFree;
  return stationPricing;
}

async function getTouSchedules() {
  const response = await API.get(`${DATA_API_SERVICE}/pricing/tou/schedule`);
  return response.data;
}

async function getUtilityTouSchedule(utility: string) {
  const response = await API.post(`${DATA_API_SERVICE}/pricing/tou/rates/default`, { utility });
  return response.data;
}

async function getPricingGeneric(entityType: EntityType, entityId: string) {
  try {
    const response = await API.get<PricingAPIResponse>(`${DATA_API_SERVICE}/pricing/entity/${entityId}`);
    return mapPricingDataFromAPI(entityType, entityId, response.data);
  } catch (e) {
    if (e.response.status !== 404) throw e;
    // No pricing information found, so we make a default pricing model
    return new Pricing({
      entity: entityType,
      entityId
    });
  }
}

async function updatePricing(pricing: Pricing) {
  // Clean pricing details:
  if (pricing.policy !== "TOU") {
    pricing.jsonData = null;
  }

  // If we want to inherit pricing, we need to deactivate the pricing
  if (pricing.inheritPricing) {
    if (pricing.activePolicyId) {
      const request = {
        id: pricing.activePolicyId,
        entity: pricing.entity,
        entityId: pricing.entityId
      };
      await API.delete(`${DATA_API_SERVICE}/pricing`, { data: request });
      return true;
    } else {
      return true; // nothing to do here literally.
    }
  }

  // If we aren't inheriting, just simply update the pricing
  try {
    const newPricingData = new Pricing(pricing);
    await API.post(`${DATA_API_SERVICE}/pricing`, newPricingData);
  } catch (e) {
    throw new Error(`Unable to update ${pricing.entityName} pricing.`);
  }
  return true;
}

async function getPricing(entityType: EntityType, entityId: string) {
  if (entityType === EntityType.STATION) {
    return getStationPricing(entityId);
  } else if (entityType === EntityType.PROPERTY) {
    return getPricingGeneric(entityType, entityId);
  } else if (entityType === EntityType.ORGANIZATION) {
    return getPricingGeneric(entityType, entityId);
  }
}

async function getPricingHistory(
  query?: Partial<{
    entityId: string | null;
    start: Date | null;
    end: Date | null;
  }>
) {
  let startDate = subDays(new Date(), 7);
  let endDate = endOfDay(new Date());
  if (query) {
    if (query.start) startDate = query.start;
    if (query.end) endDate = query.end;
  }
  try {
    const pricingHistoryResponse = await API.post<PricingHistoryItem[]>(`${DATA_API_SERVICE}/pricing/history`, {
      ...query,
      start: startDate,
      end: endDate
    });
    const pricingHistory = pricingHistoryResponse.data.map(response => {
      return new PricingHistoryItem({
        ...response
      });
    });
    return sortBy(pricingHistory, pricingItem => pricingItem.createdAt).reverse();
  } catch (e) {
    return [];
  }
}

async function getTransactionsForId(entityType?: string, entityId?: string) {
  const res = await apolloClient.query({
    query: gql`
      query AllTransactions($filter: TransactionHistoryFilter) {
        allTransactions(filter: $filter) {
          id
          transactionId: ocppTransactionId
          chargeStartTime: timeDate
          auth: rfidAuthentication
          session: sessionId
          userId
          pricingPolicy
          price: rate
          connect: connectedAt
          disconnect: disconnectedAt
          chargeTimeMinutes
          meterStart
          meterStop
          full: fullAt
          amount
          kWh
          chargingMin
          chargingMax
          isPaid
          unpluggedAt
          station {
            serialNo: serialNumber
            qrCode
            floor
            parkingSpace
          }
        }
      }
    `,
    variables: {
      filter: {
        entityType,
        entityId
      }
    }
  });
  const stationTransactions = res.data.allTransactions.map(transaction => {
    return new StationTransaction({ ...transaction, ...transaction.station });
  });
  return sortBy(stationTransactions, transaction => transaction.connect).reverse();
}

async function getSubTransactionsForId(transactionId: string) {
  const subtransactionsResponse = await API.get<StationSubTransactionAPIResponse[]>(
    `${DATA_API_SERVICE}/v2/transaction/subtransaction/${transactionId}`
  );
  const stationSubTransactions = subtransactionsResponse.data.map(subtransaction => {
    return new StationSubTransaction(subtransaction);
  });
  return sortBy(stationSubTransactions, subtransaction => subtransaction.startAt).reverse();
}

async function getTOUTransactionReport(
  query?: Partial<{
    start: Date;
    end: Date;
    stationUUID: string | null;
    propertyUUID: string | null;
  }>
) {
  let startDate = subDays(new Date(), 7);
  let endDate = endOfDay(new Date());
  if (query) {
    if (query.start) startDate = query.start;
    if (query.end) endDate = query.end;
  }
  try {
    const response = await API.post(`${DATA_API_SERVICE}/transaction/subtransaction/usage`, {
      start: startDate,
      end: endDate,
      ...query
    });
    return response.data;
  } catch (e) {
    return [];
  }
}

const APIPricing = {
  getPricing,
  getPricingHistory,
  getTransactionsForId,
  getSubTransactionsForId,
  getTOUTransactionReport,
  getTouSchedules,
  getUtilityTouSchedule,
  updatePricing
};

export default APIPricing;
